import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export function ModificaOreGiorno({ open, onClose, mese, dipendente, commessa }) {
  const qc = useQueryClient();
  const [righe, setRighe] = useState([]);
  const { data, isLoading } = useQuery({
    queryKey: ["ore-mensili-dettaglio", mese, dipendente?.dipendente_id, commessa?.commessa_id],
    queryFn: async () => (await api.get("/ore-mensili/dettaglio", { params: { mese, dipendente_id: dipendente.dipendente_id, commessa_id: commessa.commessa_id } })).data,
    enabled: open && !!dipendente && !!commessa,
  });
  useEffect(() => { setRighe((data || []).map((r) => ({ ...r, modificata: false }))); }, [data]);

  const mut = useMutation({
    mutationFn: async (r) => (await api.put("/ore-mensili/giorno", {
      dipendente_id: dipendente.dipendente_id, commessa_id: commessa.commessa_id, data: r.data,
      ore_ordinarie: Number(r.ore_ordinarie) || 0, ore_straordinarie: Number(r.ore_straordinarie) || 0,
      pasto: !!r.pasto, cena: !!r.cena, notti: Number(r.notti) || 0, note: r.note || "",
    })).data,
    onSuccess: () => { toast.success("Registrazione aggiornata"); qc.invalidateQueries({ queryKey: ["ore-mensili"] }); qc.invalidateQueries({ queryKey: ["ore-mensili-dettaglio"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (i, k, v) => setRighe((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v, modificata: true } : r)));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="modifica-ore-giorno-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Modifica ore · {dipendente?.nome}</DialogTitle>
          <DialogDescription>{commessa?.codice} — {commessa?.descrizione}. Una riga per giorno: modifica e salva.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="py-8 text-center text-slate-400">Caricamento…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b">
                <th className="py-2 pr-2">Data</th>
                <th className="py-2 pr-2 text-right">Ore ord.</th>
                <th className="py-2 pr-2 text-right">Ore str.</th>
                <th className="py-2 pr-2 text-center">Pasto</th>
                <th className="py-2 pr-2 text-center">Cena</th>
                <th className="py-2 pr-2 text-center">Notte</th>
                <th className="py-2 pr-2">Note</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r, i) => (
                <tr key={r.data} className="border-b border-slate-50" data-testid={`ore-giorno-riga-${r.data}`}>
                  <td className="py-2 pr-2 font-mono whitespace-nowrap">{dateIt(r.data)}</td>
                  <td className="py-2 pr-2"><Input type="number" min="0" max="8" step="0.5" value={r.ore_ordinarie} onChange={(e) => set(i, "ore_ordinarie", e.target.value)} className="w-20 text-right ml-auto" data-testid={`ore-giorno-ord-${r.data}`} /></td>
                  <td className="py-2 pr-2"><Input type="number" min="0" max="16" step="0.5" value={r.ore_straordinarie} onChange={(e) => set(i, "ore_straordinarie", e.target.value)} className="w-20 text-right ml-auto" data-testid={`ore-giorno-str-${r.data}`} /></td>
                  <td className="py-2 pr-2 text-center"><Checkbox checked={!!r.pasto} onCheckedChange={(v) => set(i, "pasto", !!v)} data-testid={`ore-giorno-pasto-${r.data}`} /></td>
                  <td className="py-2 pr-2 text-center"><Checkbox checked={!!r.cena} onCheckedChange={(v) => set(i, "cena", !!v)} data-testid={`ore-giorno-cena-${r.data}`} /></td>
                  <td className="py-2 pr-2 text-center"><Checkbox checked={Number(r.notti) > 0} onCheckedChange={(v) => set(i, "notti", v ? 1 : 0)} data-testid={`ore-giorno-notte-${r.data}`} /></td>
                  <td className="py-2 pr-2"><Input value={r.note || ""} onChange={(e) => set(i, "note", e.target.value)} className="min-w-[140px]" data-testid={`ore-giorno-note-${r.data}`} /></td>
                  <td className="py-2 text-right">
                    <Button size="sm" disabled={!r.modificata || mut.isPending} onClick={() => mut.mutate(r)} data-testid={`ore-giorno-salva-${r.data}`}>Salva</Button>
                  </td>
                </tr>
              ))}
              {righe.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">Nessuna registrazione.</td></tr>}
            </tbody>
          </table>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ore-giorno-chiudi">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
